import React, { useState } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import {
  Card,
  PrimaryButton,
  ButtonIcon,
  Link,
  TextRegular,
  TextLarge,
  Icon,
  UtilsBlock,
} from '@hz-design-system/web-ui';
import { ReviewsDialog, type UserReviews } from '@ecg-internal/reviews-dialog-ui';

import SaveSellerControl from '../SaveSellerControl/SaveSellerControl';
import getDisplayReviewData from '../../../utils/getDisplayReviewData';
import trackEvent from '../../../utils/trackEvent';
import {
  SELLER_REVIEW_CLICK,
  SELLER_REVIEW_TAB_CLICK,
  SELLER_REVIEW_LOCATION_CLICK,
  SELLER_REVIEW_LOCATION_CHANGE,
  SELLER_REVIEW_EXTERNAL_LINK_CLICK,
} from '../../../constants/gaConstants';
import { buildIFrameSrc } from '../../../utils/iframeUtils';

import Classes from './Info.module.scss';

type InfoProps = {
  sellerId: number;
  sellerName: string;
  isUserViewingOwnProfile: boolean;
  editProfileUrl: string;
  isSellerSaved: boolean;
  isSaveSellerControlVisible: boolean;
  periodSinceRegistrationDate: string;
  onClickSaveControl: () => void;
  saveControlLoading?: boolean;
  reviews: UserReviews[];
  location?: string;
  hasSellerProfile?: boolean;
};

const Info = ({
  sellerId,
  sellerName,
  isUserViewingOwnProfile,
  editProfileUrl,
  isSellerSaved,
  isSaveSellerControlVisible,
  periodSinceRegistrationDate,
  onClickSaveControl,
  saveControlLoading = false,
  reviews,
  hasSellerProfile,
}: InfoProps) => {
  const i18n = useI18nContext();
  const [isReviewsDialogOpen, setReviewsDialogOpen] = useState(false);

  const firstReview = reviews[0];
  const { averageScore, numberOfReviews, withReviewAttribution } = getDisplayReviewData(firstReview);
  const sameSystemCount = reviews.filter(
    (r) => firstReview?.reviewSystem && r.reviewSystem === firstReview.reviewSystem,
  ).length; // happens on multi location sellers

  const handleClickButton = (evt: any) => {
    evt.preventDefault();
    onClickSaveControl();
  };

  const handleClickReviews = (evt: any) => {
    evt.preventDefault();

    if (averageScore) {
      trackEvent({
        eventAction: SELLER_REVIEW_CLICK,
        eventLabel: firstReview?.reviewSystem ?? '',
      });
    }

    setReviewsDialogOpen(true);
  };

  const handleTabClick = (reviewSystem: string | undefined) =>
    trackEvent({
      eventAction: SELLER_REVIEW_TAB_CLICK,
      eventLabel: reviewSystem ?? '',
    });

  const handleLocationFocus = () =>
    trackEvent({
      eventAction: SELLER_REVIEW_LOCATION_CLICK,
    });

  const handleLocationChange = (locationName: string) =>
    trackEvent({
      eventAction: SELLER_REVIEW_LOCATION_CHANGE,
      eventLabel: locationName,
    });

  const renderSaveSellerControl = () =>
    isSaveSellerControlVisible && (
      <SaveSellerControl
        sellerId={sellerId}
        isSellerSaved={isSellerSaved}
        onClick={handleClickButton}
        loading={saveControlLoading}
      />
    );

  const textJoinedSince = i18n.t('sellerHeader.joinedSince', {
    value: periodSinceRegistrationDate,
  });

  const redirectToEditSellerProfile = () => window.location.assign(editProfileUrl);

  return (
    <>
      {isReviewsDialogOpen && reviews.length > 0 && (
        <ReviewsDialog
          onClose={() => setReviewsDialogOpen(false)}
          reviews={reviews}
          sellerName={sellerName}
          internalReviewsUrl={buildIFrameSrc(sellerId)}
          onTabClick={handleTabClick}
          onExternalReviewsUrlClick={() => trackEvent({ eventAction: SELLER_REVIEW_EXTERNAL_LINK_CLICK })}
          onLocationChange={handleLocationChange}
          onLocationFocus={handleLocationFocus}
        />
      )}
      <Card className={`${Classes.root} ${hasSellerProfile ? '' : Classes.noProfile}`}>
        <div>
          <div className={Classes.text}>{textJoinedSince}</div>
          {!!averageScore && (
            <UtilsBlock textStyle="body-base-regular">
              <Icon name="saved" color="text-brand-primary" />

              {sameSystemCount > 1 ? (
                <>
                  <Link onClick={handleClickReviews}>{i18n.t('sellerHeader.multiLocationReview.link')}</Link>{' '}
                  <TextLarge>{i18n.t('sellerHeader.multiLocationReview.copy', { count: sameSystemCount })}</TextLarge>
                </>
              ) : (
                <>
                  <TextLarge strong>{`${averageScore.toFixed(1)} · `}</TextLarge>
                  <Link onClick={handleClickReviews}>
                    {`${numberOfReviews} ${i18n.t('sellerHeader.review', { count: numberOfReviews })}`}
                  </Link>
                  {withReviewAttribution && (
                    <UtilsBlock as="span" marginTop="xxxs" className={Classes.reviewAttribution}>
                      <Icon name="info" color="text-secondary" className="u-marginRightXxxxs" />
                      <TextRegular color="text-secondary">Google Maps</TextRegular>
                    </UtilsBlock>
                  )}
                </>
              )}
            </UtilsBlock>
          )}
        </div>
        {isUserViewingOwnProfile && (
          <div className={Classes.buttonWrap}>
            <PrimaryButton onClick={redirectToEditSellerProfile}>
              <ButtonIcon name="edit" isStartIcon={true} />
              {i18n.t('sellerHeader.buttonEditProfile')}
            </PrimaryButton>
          </div>
        )}
        {!isUserViewingOwnProfile && <div className={Classes.buttonWrap}>{renderSaveSellerControl()}</div>}
      </Card>
    </>
  );
};

export default Info;
